/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { GrantTokenResponseDto } from '@ink-ai/insight-service-sdk';
import { AppParam } from '../reducers/app';
import { jwtDecode } from 'jwt-decode';

const APP_PARAM_TOKEN = 'INK_AI_APP_PARAM_20250331';

// We use id token for authentication, use expire time in id token by default
export const getExpiresAt = (authBody: GrantTokenResponseDto) => {
  if (!authBody.id_token) {
    return authBody.expires_at;
  }
  const idToken = jwtDecode(authBody.id_token);
  return idToken.exp ?? authBody.expires_at;
};

export const getTokens = (): GrantTokenResponseDto => ({
  access_token: localStorage.getItem('accessToken'),
  refresh_token: localStorage.getItem('refreshToken'),
  id_token: localStorage.getItem('idToken'),
  expires_at: parseInt(localStorage.getItem('expiresAt')),
});

export const storeTokens = (grantTokenResp: GrantTokenResponseDto | null) => {
  if (grantTokenResp === null) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('expiresAt');
    return;
  }
  localStorage.setItem('accessToken', grantTokenResp.access_token);
  grantTokenResp.refresh_token &&
    localStorage.setItem('refreshToken', grantTokenResp.refresh_token);
  localStorage.setItem('idToken', grantTokenResp.id_token);
  localStorage.setItem('expiresAt', getExpiresAt(grantTokenResp).toString());
};

export const clearAll = () => localStorage.clear();

export const storeAppParam = (appParam: AppParam) =>
  localStorage.setItem(APP_PARAM_TOKEN, JSON.stringify(appParam));

export const getAppParam = (): AppParam | null => {
  const appParamString = localStorage.getItem(APP_PARAM_TOKEN);
  if (appParamString) {
    return JSON.parse(appParamString);
  }
  return null;
};

const LANGUAGE_KEY = 'INK_AI_LANGUAGE';

export const getStoredLanguage = (): string => {
  return localStorage.getItem(LANGUAGE_KEY) || 'en';
};

export const storeLanguage = (language: string) => {
  localStorage.setItem(LANGUAGE_KEY, language);
};
